import {
  Alert,
  Box,
  Button,
  Table,
  Title,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import {
  AccountingDraftEntriesCountWidgetDocument,
  GetExpensablesDocument,
  InvoicesDetailPageAvailabilitiesDocument,
  InvoicesDetailPageInvoiceDocument,
  JournalDraftEntryFragment,
  JournalDraftEntryLineFragment,
  RecordJournalDraftEntryDocument,
  UpdateJournalDraftEntryDocument,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { FormErrors } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { LegalEntity } from 'modules/finance/components/Invoices/Detail/Content/Form/types'

import { JournalDraftEntryLineRow } from './JournalDraftEntryLineRow'
import { JournalEntryData } from './JournalEntryData'
import { JournalLineFooter } from './JournalLineFooter'
import { JournalDraftEntryFormValues } from './form'

interface Props {
  journalDraftEntry?: JournalDraftEntryFragment | null
  legalEntity?: LegalEntity | null
  currency: string
  onRecord?: () => void
  refetchQueriesOnRecord?: DocumentNode[]
}

export const JournalDraftEntryTab = ({
  journalDraftEntry,
  legalEntity,
  currency,
  onRecord,
  refetchQueriesOnRecord = [
    InvoicesDetailPageAvailabilitiesDocument,
    InvoicesDetailPageInvoiceDocument,
    GetExpensablesDocument,
  ],
}: Props) => {
  const { access } = useCurrentSession()
  const { openDialog } = useConfirmationDialog()
  const locale = access.locale

  const [updateJournalDraftEntry, { loading: updating }] = useMutation(
    UpdateJournalDraftEntryDocument,
    {
      refetchQueries: [AccountingDraftEntriesCountWidgetDocument],
      extractorFn: (data) => data.financeMutations.updateFinanceJournalDraftEntry,
    }
  )

  const [recordJournalDraftEntry, { loading: recording }] = useMutation(
    RecordJournalDraftEntryDocument,
    {
      extractorFn: (data) => data.financeMutations.recordDraftFinanceJournalEntry,
      refetchQueries: refetchQueriesOnRecord,
    }
  )

  const loading = updating || recording

  const form = useForm<JournalDraftEntryFormValues>({
    enableReinitialize: true,
    validateOnChange: true,
    defaultValues: {
      journalDraftEntryLines: journalDraftEntry?.journalDraftLines || [],
      journalDraftEntryLinesToDelete: [],
      referenceDate: journalDraftEntry?.referenceDate || '',
      description: journalDraftEntry?.description || '',
    },

    validate: (_data) => {
      const errors: FormErrors<JournalDraftEntryFormValues> = {}

      return errors
    },

    onSubmit: async (data) => {
      if (!journalDraftEntry?.id) return { success: true, data: data }

      const lines = data.journalDraftEntryLines.map((line) => {
        return {
          id: line.id && !line.id.startsWith('-') ? line.id : null,
          accountId: line.account?.id,
          creditAmountCents: line.creditAmountCents || 0,
          debitAmountCents: line.debitAmountCents || 0,
        }
      })

      const result = await updateJournalDraftEntry({
        variables: {
          journalDraftEntryId: journalDraftEntry?.id,
          lines: lines,
          linesIdsToDelete: data.journalDraftEntryLinesToDelete,
          referenceDate: data.referenceDate,
          description: data.description,
        },
      })

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: errors.base || errors.messages || i18n.t('errors.generic_error'),
        })
      })
      if (!handled.success) return handled

      track(trackingEvents.accounting.accounting.saveDraftEntry, {
        status: handled.data.journalDraftEntry?.status,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.journal_entry.update_success_v2'),
      })

      return handled
    },
  })

  const handleRowUpdated = (updatedJournalDraftEntryLine: JournalDraftEntryLineFragment) => {
    const newJournalDraftEntryLines = form.values.journalDraftEntryLines.map(
      (journalDraftEntryLine) => {
        if (journalDraftEntryLine.id === updatedJournalDraftEntryLine.id) {
          return updatedJournalDraftEntryLine
        }
        return journalDraftEntryLine
      }
    )
    form.setValues({ ...form.values, journalDraftEntryLines: newJournalDraftEntryLines })
  }

  const handleRowDeleted = (deletedJournalDraftEntryLine: JournalDraftEntryLineFragment) => {
    const newJournalDraftEntryLines = form.values.journalDraftEntryLines.filter(
      (journalDraftEntryLine) => journalDraftEntryLine.id !== deletedJournalDraftEntryLine.id
    )

    const newJournalDraftEntryLinesToDelete =
      deletedJournalDraftEntryLine.id && !deletedJournalDraftEntryLine.id.startsWith('-')
        ? [...form.values.journalDraftEntryLinesToDelete, deletedJournalDraftEntryLine.id]
        : [...form.values.journalDraftEntryLinesToDelete]
    form.setValues({
      ...form.values,
      journalDraftEntryLines: newJournalDraftEntryLines,
      journalDraftEntryLinesToDelete: newJournalDraftEntryLinesToDelete,
    })
  }

  const handleRowAdded = () => {
    const newJournalDraftEntryLine: JournalDraftEntryLineFragment = {
      id: String((Date.now() + Math.floor(Math.random() * 10000)) * -1),
      account: null,
      debitAmountCents: 0,
      creditAmountCents: 0,
    }
    form.setValues({
      ...form.values,
      journalDraftEntryLines: [...form.values.journalDraftEntryLines, newJournalDraftEntryLine],
    })
  }

  const formIsValidForRecord = (): boolean => {
    if (!form.values.referenceDate) return false

    if (form.values.journalDraftEntryLines.length == 0) return false

    let totalDebit = 0
    let totalCredit = 0

    for (const line of form.values.journalDraftEntryLines) {
      totalDebit += line.debitAmountCents || 0
      totalCredit += line.creditAmountCents || 0

      if (!line.account || (line.debitAmountCents === 0 && line.creditAmountCents === 0)) {
        return false
      }
    }

    if (totalDebit !== totalCredit) return false
    return true
  }

  const handleRecordDraftEntry = () => {
    openDialog({
      illustration: 'alert',
      title: i18n.t('finance.journal_entry.record.confirmation_dialog.title'),
      content: i18n.t('finance.journal_entry.record.confirmation_dialog.content_v2'),
      confirmLabel: i18n.t('finance.journal_entry.record.confirmation_dialog.record'),
      cancelLabel: i18n.t('finance.journal_entry.record.confirmation_dialog.cancel'),
      onConfirm: confirmRecordDraftJournalEntry,
    })
  }

  const confirmRecordDraftJournalEntry = async () => {
    if (!journalDraftEntry?.id) return

    const result = await recordJournalDraftEntry({
      variables: {
        journalDraftEntryId: journalDraftEntry?.id,
      },
    }).then((res) => {
      track(trackingEvents.accounting.accounting.publishDraftEntry)
      return res
    })

    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (!handled.success) return handled

    onRecord?.()
    app.setFlash({
      type: 'correct',
      message: i18n.t('finance.journal_entry.record_success_v2'),
    })

    return handled
  }

  return (
    <Box gap={'s16'}>
      {journalDraftEntry?.type && (
        <JournalEntryData
          form={form}
          currency={currency}
          legalEntityName={legalEntity?.legalName || ''}
          type={journalDraftEntry?.type}
          status={journalDraftEntry?.status}
        />
      )}

      <Box gap="s4">
        <Title
          variant="400"
          title={i18n.t('finance.journal_entry.title')}
          description={i18n.t('finance.journal_entry.description')}
        />

        <Alert color="info">{i18n.t('finance.journal_entry.hint_draft_v2')}</Alert>
      </Box>
      <Table
        style={{ flex: '1', overflowY: 'auto', height: 'auto' }}
        optionsBottom={() => (
          <JournalLineFooter
            journalEntryLines={form.values.journalDraftEntryLines}
            locale={locale}
            currency={currency}
          />
        )}
        bottomSticky
        autoLayout
      >
        <Table.Head>
          <Table.Row>
            <Table.HeadCell paddingX="s4" ellipsis>
              <Box style={{ paddingLeft: '12px' }}>
                {i18n.t('finance.journal_entry.journal_line.header.account')}
              </Box>
            </Table.HeadCell>
            <Table.HeadCell paddingX="s24" ellipsis width={130}>
              <Box style={{ paddingRight: '20px' }}>
                {i18n.t('finance.journal_entry.journal_line.header.debit')}
              </Box>
            </Table.HeadCell>
            <Table.HeadCell paddingX="s24" ellipsis width={130}>
              <Box style={{ paddingRight: '20px' }}>
                {i18n.t('finance.journal_entry.journal_line.header.credit')}
              </Box>
            </Table.HeadCell>
            <Table.HeadCell paddingX="s6" style={{ width: '1px', whiteSpace: 'nowrap' }}>
              <Box overflowY="auto" style={{ scrollbarGutter: 'stable', width: '40px' }} />
            </Table.HeadCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {legalEntity &&
            form.values.journalDraftEntryLines.map((journalDraftEntryLine) => (
              <JournalDraftEntryLineRow
                key={journalDraftEntryLine.id}
                lineId={journalDraftEntryLine.id}
                legalEntityId={legalEntity.id}
                onSelectLedgerAccount={(id) => {
                  handleRowUpdated({
                    ...journalDraftEntryLine,
                    account: { id: id },
                  })
                }}
                selectedLedgerAccountId={journalDraftEntryLine.account?.id}
                debitAmountCents={journalDraftEntryLine.debitAmountCents || 0}
                creditAmountCents={journalDraftEntryLine.creditAmountCents || 0}
                ledgerAccounts={legalEntity?.financeAccounts}
                disabled={false}
                onChangeDebitAmount={(value) => {
                  handleRowUpdated({
                    ...journalDraftEntryLine,
                    debitAmountCents: value,
                    creditAmountCents: 0,
                  })
                }}
                onChangeCreditAmount={(value) => {
                  handleRowUpdated({
                    ...journalDraftEntryLine,
                    debitAmountCents: 0,
                    creditAmountCents: value,
                  })
                }}
                onDelete={() => handleRowDeleted(journalDraftEntryLine)}
                currency={currency}
              />
            ))}
          <Table.Row key={'add_line'}>
            <Table.Cell padding="s12" colSpan={4}>
              <Button
                onClick={() => handleRowAdded()}
                type="secondary"
                disabled={loading}
                iconName="add"
              >
                {i18n.t('finance.journal_entry.journal_line.add_line')}
              </Button>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <Box flexDirection="row" justifyContent="flexEnd" padding="s16">
        {form.dirty && (
          <Box flexDirection="row" gap="s8">
            <Button onClick={() => form.resetForm()} type="secondary" disabled={loading}>
              {i18n.t('common.cancel')}
            </Button>
            <Button
              onClick={form.submitForm}
              type="primary"
              disabled={!form.dirty || loading}
              loading={loading}
            >
              {i18n.t('common.save')}
            </Button>
          </Box>
        )}
        {!form.dirty && formIsValidForRecord() && (
          <Button
            onClick={handleRecordDraftEntry}
            type="positive"
            disabled={form.dirty || !formIsValidForRecord() || loading}
            loading={loading}
          >
            {i18n.t('finance.journal_entry.record.button_title')}
          </Button>
        )}
      </Box>
    </Box>
  )
}
